import React, { useState, useEffect } from "react";
import Select from "react-select";
import { db } from "../firebase";
import {
  collection,
  setDoc,
  doc,
  getDocs,
  getDoc,
} from "firebase/firestore";

function normalizeName(name) {
  return name
    .toLowerCase()
    .replace(/\.(com|net|org|co|io|us|biz|info|gov|edu)\b/g, "")
    .replace(/\b(group|inc|llc|corp|corporation|limited|co|company|store|stores|shop|market)\b/g, "")
    .replace(/[^a-z0-9]/g, "")
    .trim();
}

const cleanVendor = (obj) => {
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    const isEmptyString = typeof value === "string" && value.trim() === "";
    const isEmptyArray = Array.isArray(value) && value.length === 0;

    if (value === undefined || value === null || isEmptyString || isEmptyArray) {
      continue;
    }
    cleaned[key] = value;
  }
  return cleaned;
};


export default function AddVendor() {
  const [vendorName, setVendorName] = useState("");
  const [address, setAddress] = useState("");
  const [logo, setLogo] = useState("");
  const [trackingId, setTrackingId] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [existingNormalizedNames, setExistingNormalizedNames] = useState([]);

  // Load categories and existing vendors on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snap = await getDocs(collection(db, "category"));
        const all = [];
        snap.forEach((doc) => {
          const data = doc.data();
          if (Array.isArray(data.categories)) all.push(...data.categories);
        });
        setCategoryList([...new Set(all)].sort((a, b) => a.localeCompare(b)));
      } catch (err) {
        console.error("Failed to load categories:", err);
      }
    };

    const fetchExistingVendors = async () => {
      try {
        const batchDocsSnap = await getDocs(collection(db, "vendors"));
        const allNormalized = [];

        for (const docSnap of batchDocsSnap.docs) {
          const data = docSnap.data();
          if (Array.isArray(data.vendors)) {
            data.vendors.forEach((v) => {
              if (v.vendorName) allNormalized.push(normalizeName(v.vendorName));
            });
          }
        }

        setExistingNormalizedNames(allNormalized);
      } catch (err) {
        console.error("Failed to load existing vendors:", err);
      }
    };

    fetchCategories();
    fetchExistingVendors();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setMessage("");
  };

  // Convert category strings into react-select option objects
  const categoryOptions = categoryList.map((cat) => ({ value: cat, label: cat }));

  // When user selects categories
  const handleCategoryChange = (selectedOptions) => {
    setCategories(selectedOptions ? selectedOptions.map((opt) => opt.value) : []);
  };

  // Determine Firestore vendor document key based on first letter of normalized name
  const getVendorDocRef = (normalizedName) => {
    const firstChar = normalizedName.charAt(0);
    const batchKey = /^[a-z]$/.test(firstChar) ? `vendors_${firstChar}` : "vendors_rest";
    return doc(db, "vendors", batchKey);
  };

  // Append vendor data to Firestore doc's vendors array (create or update)
  const appendVendorToDoc = async (vendorData, normalizedName) => {
    const vendorRef = getVendorDocRef(normalizedName);
    const snap = await getDoc(vendorRef);
    const existingVendors = snap.exists() && Array.isArray(snap.data().vendors)
      ? snap.data().vendors
      : [];

    await setDoc(vendorRef, {
      vendors: [...existingVendors, vendorData],
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!file && !vendorName.trim()) {
      setMessage("Please fill the manual form or upload a JSON file.");
      return;
    }

    if (file && vendorName.trim()) {
      setMessage("Please either fill the form or upload a JSON file, not both.");
      return;
    }

    setLoading(true);

    if (file) {
      if (file.type !== "application/json") {
        setMessage("Please upload a valid JSON file.");
        setLoading(false);
        return;
      }

      try {
        const text = await file.text();
        const data = JSON.parse(text);

        if (!Array.isArray(data)) {
          setMessage("JSON must be an array of vendor objects.");
          setLoading(false);
          return;
        }

        let successCount = 0;
        const failedVendors = [];
        const duplicateVendors = [];

        for (const vendor of data) {
          if (!vendor.vendorName) {
            failedVendors.push("Unknown Vendor");
            continue;
          }

          const vendorId = vendor.vendorName.trim();
          const normalizedName = normalizeName(vendorId);

          if (existingNormalizedNames.includes(normalizedName)) {
            duplicateVendors.push(vendorId);
            continue;
          }

          try {
            await appendVendorToDoc(
              cleanVendor({
                vendorName: vendorId,
                address: vendor.address,
                logo: vendor.logo,
                tracking_id: vendor.trackingId,
                category: vendor.category,
              }),
              normalizedName
            );
            successCount++;
          } catch {
            failedVendors.push(vendorId);
          }
        }

        setMessage(
          `Upload complete: ${successCount} vendors uploaded.` +
          (duplicateVendors.length > 0
            ? ` Skipped duplicates: ${[...new Set(duplicateVendors)].join(", ")}.`
            : "") +
          (failedVendors.length > 0
            ? ` Failed: ${[...new Set(failedVendors)].join(", ")}.`
            : "")
        );
      } catch (err) {
        setMessage("Failed to parse JSON file: " + err.message);
      }

      setFile(null);
    } else {
      // Manual form submission
      const vendorId = vendorName.trim();
      const normalizedName = normalizeName(vendorId);

      if (existingNormalizedNames.includes(normalizedName)) {
        setMessage(`Vendor "${vendorId}" is too similar to an existing vendor. Skipped.`);
        setLoading(false);
        return;
      }

      try {
        await appendVendorToDoc(
          cleanVendor({
            vendorName: vendorId,
            address: address.trim(),
            logo: logo.trim(),
            tracking_id: trackingId.trim(),
            category: categories,
          }),
          normalizedName
        );
        setMessage("Vendor added successfully!");
        setVendorName("");
        setAddress("");
        setLogo("");
        setTrackingId("");
        setCategories([]);
      } catch (error) {
        setMessage("Error uploading vendor: " + error.message);
      }
    }

    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded shadow">
      <h2 className="text-2xl font-semibold mb-4 text-center">Upload Vendor</h2>

      <form onSubmit={handleUpload} className="flex flex-col gap-4">
        <label className="flex flex-col">
          Vendor Name (Document ID):
          <input
            type="text"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            className="border rounded p-2 mt-1"
            disabled={!!file}
            required={!file}
          />
        </label>

        <label className="flex flex-col">
          Address (URL):
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="border rounded p-2 mt-1"
            disabled={!!file}
          />
        </label>

        <label className="flex flex-col">
          Logo URL:
          <input
            type="text"
            value={logo}
            onChange={(e) => setLogo(e.target.value)}
            className="border rounded p-2 mt-1"
            disabled={!!file}
          />
        </label>

        <label className="flex flex-col">
          Tracking ID (Optional):
          <input
            type="text"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            className="border rounded p-2 mt-1"
            disabled={!!file}
          />
        </label>

        <label className="flex flex-col">
          Categories (Multi-select, search existing):
          <Select
            isMulti
            options={categoryOptions}
            value={categoryOptions.filter((opt) => categories.includes(opt.value))}
            onChange={handleCategoryChange}
            isDisabled={!!file}
            placeholder="Select categories..."
            className="mt-1"
          />
        </label>

        <div className="text-center font-semibold my-2">OR</div>

        <label className="flex flex-col items-center">
          Upload JSON File (Array of Vendors):
          <input
            type="file"
            accept=".json,application/json"
            onChange={handleFileChange}
            disabled={loading}
          />
          {file && <p className="mt-1 text-sm text-gray-600">{file.name}</p>}
        </label>

        <button
          type="submit"
          disabled={loading}
          className="bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 disabled:opacity-50 transition"
        >
          {loading ? "Uploading..." : "Upload Vendor"}
        </button>
      </form>

      {message && (
        <p className="mt-4 text-center text-sm text-red-600 whitespace-pre-line">
          {message}
        </p>
      )}
    </div>
  );
}
